import React, { useState, useContext, useRef, useEffect } from "react";
import {
  Form,
  Input,
  Button,
  message,
  Select,
  Upload,
  DatePicker,
  Typography,
  Col,
  Row,
  Modal,
  Space,
} from "antd";
import inputStyles from "../../../styles/Custom/Input.module.css";
import btnStyles from "../../../styles/Custom/Button.module.css";
import { UserContext } from "../../../context/UserContext";
import {
  patientInvestigationsOrderAPI,
  patientInvestigationsOrderUpdateAPI,
} from "../../../services/patient.services";
import { getCookie } from "../../../helpers/localStorage";
import {
  CameraOutlined,
  CloseCircleFilled,
  CloseOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { FacilityID, Role } from "../../../constants/defaultKeys";
import { StatusOptions } from "../../../helpers/medicineKeys";
import InvestigationSearch from "./InvestigationSearch";
import { getUploadUrlApi } from "../../../services/common.services";
import { convertToFormData } from "../../../helpers/utility";
import axios from "axios";
import dayjs from "dayjs";
import useQuery from "../../../hooks/useQuery";
import useIsMobile from "../../../hooks/useIsMobile";
import useReceptionistMobilePickerProps from "../../Receptionist/receptionistMobilePickerProps";
import { alignInvestigationDatePickerBelow } from "./InitialInvestigationForm";
import "../../../components/Receptionist/receptionistMobileForms.css";
import "./InvestigationModal.css";

const { Option } = Select;
const { Dragger } = Upload;
const InvestigationModal = ({ open, setOpen, refreshPage, editData, id }) => {
  const isMobile = useIsMobile();
  const mobilePickerProps = useReceptionistMobilePickerProps();
  const datePickerPopupClass = [
    "investigation-date-picker-dropdown",
    mobilePickerProps.popupClassName,
  ]
    .filter(Boolean)
    .join(" ");
  const isCompactPicker = Object.keys(mobilePickerProps).length > 0;
  const { userDetails } = useContext(UserContext);
  const [form] = Form.useForm();
  const formRef = useRef(null);
  const [searchedText, setSearchedText] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadFile, setUploadFile] = useState([]);
  const [uploadFileLoading, setUploadFileLoading] = useState(false);
  const params = useQuery();

  function disabledDate(current) {
    return current && current > dayjs().endOf("day");
  }

  useEffect(() => {
    if (editData) {
      form.setFieldsValue({
        ...editData,
        investigation: editData?.investigation_name || editData?.name,
        delivery_on: editData?.delivery_on
          ? dayjs(editData?.delivery_on)
          : null,
      });
      setSelectedId(editData?.investigation || editData?.investigation_id);
      setSelectedStatus(editData?.status);
      setUploadFile(editData?.report_path || []);
    }
  }, [editData]);
  const MAX_FILE_SIZE_MB = 30;
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

  const uploadProps = {
    beforeUpload: (file) => {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        message.error(`File must be ${MAX_FILE_SIZE_MB}MB or smaller`);
        return Upload.LIST_IGNORE;
      }
      let file_ext = file.name.split(".").slice(-1)[0];
      const fileType =
        file_ext === "png" ||
        file_ext === "jpg" ||
        file_ext === "jpeg" ||
        file_ext === "svg" ||
        file.type === "application/pdf";

      if (!fileType) {
        message.error(`${file.name} is not a valid file`);
      }
      return fileType ? true : Upload.LIST_IGNORE;
    },
    async onChange(info) {
      if (info.file.status === "done") {
        setUploadFileLoading(true);
        try {
          const res = await getUploadUrlApi(
            `path_key=${info.file.name}&admission_type=IP&admission_id=${params?.patient}`
          );
          const formData = convertToFormData(res, info);
          await axios({
            method: "POST",
            url: res?.data?.url,
            data: formData,
            headers: {
              "Content-Type": "multipart/form-data",
            },
          });
          setUploadFile((prevState) =>
            prevState
              ? [
                  ...prevState,
                  { path: res?.data?.fields?.key, type: info.file.type },
                ]
              : [{ path: res?.data?.fields?.key, type: info.file.type }]
          );
        } catch (err) {
          message.error(err?.response?.data?.message);
        } finally {
          setUploadFileLoading(false);
        }
      }
    },
  };

  const dummyRequest = ({ file, onSuccess }) => {
    setTimeout(() => {
      onSuccess("ok");
    }, 0);
  };

  const onFinish = (values) => {
    setLoading(true);
    const formData = {
      facility: Number(getCookie(FacilityID)) || userDetails?.currFacility,
      investigation: selectedId,
      patient: Number(id),
      ip_admission: Number(id),
      ordered_by: userDetails?.id,
      comments: values?.comments,
      status: values?.status,
      report_path: uploadFile,
      delivery_on: values?.delivery_on,
    };

    if (selectedId) {
      const apiCall = editData?.id
        ? patientInvestigationsOrderUpdateAPI(editData?.id, formData)
        : patientInvestigationsOrderAPI(formData);
      apiCall
        .then((res) => {
          message.success(res?.data?.message);
          setLoading(false);
          setOpen(false);
          refreshPage();
        })
        .catch((err) => {
          message.error(err?.response?.data?.message);
          setLoading(false);
        });
    } else {
      message.error("Please select a valid Investigation ID");

      setLoading(false);
    }
  };

  const handleRemoveFile = (fileToRemove) => {
    const filteredFiles = uploadFile.filter(
      (file) => file.path !== fileToRemove.path
    );
    setUploadFile(filteredFiles);
    message.success("Removed file successfully");
  };

  const defaultRole = getCookie(Role) || userDetails?.currRole;
  return (
    <>
      <Modal
        title={
          <Row>
            <Col span={20} className="flex-start">
              <Typography
                style={{
                  marginLeft: isMobile ? 0 : "12px",
                  fontWeight: "400",
                  fontSize: "18px",
                }}
              >
                {editData?.id ? "Edit Investigation" : "Request Investigation"}
              </Typography>
            </Col>
            <Col span={4} className="flex-end">
              <CloseOutlined
                onClick={() => {
                  setOpen(false);
                }}
              />
            </Col>
          </Row>
        }
        closeIcon={null}
        centered={false}
        width={isMobile ? "100%" : "30%"}
        style={isMobile ? undefined : { top: "8px" }}
        wrapClassName={
          isMobile
            ? "reception-modal-fullscreen investigation-modal-mobile-wrap"
            : undefined
        }
        classNames={
          isMobile
            ? {
                wrapper:
                  "reception-modal-fullscreen investigation-modal-mobile-wrap",
              }
            : undefined
        }
        className={isMobile ? "investigation-modal-mobile" : undefined}
        styles={
          isMobile
            ? {
                wrapper: { padding: 0, overflow: "hidden" },
                content: {
                  height: "100%",
                  maxHeight: "100dvh",
                  margin: 0,
                  top: 0,
                  padding: 0,
                  borderRadius: 0,
                },
                header: {
                  padding: "16px 20px 12px",
                  marginBottom: 0,
                },
                body: {
                  flex: 1,
                  overflow: "auto",
                  padding: "12px 20px 24px",
                },
                footer: {
                  padding:
                    "12px 20px calc(16px + env(safe-area-inset-bottom, 0px))",
                  marginTop: 0,
                },
              }
            : undefined
        }
        open={open}
        footer={
          isMobile ? (
            <div className="investigation-modal-footer">
              <Space size={10}>
                <Button
                  type="primary"
                  ghost
                  style={{ fontWeight: "500" }}
                  className={btnStyles.mdBtn}
                  onClick={() => setOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  loading={loading}
                  type="primary"
                  style={{ fontWeight: "500" }}
                  className={btnStyles.mdBtn}
                  onClick={() => {
                    if (formRef.current) {
                      formRef.current.submit();
                    }
                  }}
                >
                  Save
                </Button>
              </Space>
            </div>
          ) : (
            <div
              className="flex-end investigation-modal-footer"
              style={{ gap: "24px", marginTop: "20px" }}
            >
              <Form.Item>
                <Button
                  type="primary"
                  ghost
                  style={{ fontWeight: "500" }}
                  className={btnStyles.mdBtn}
                  onClick={() => setOpen(false)}
                >
                  Cancel
                </Button>
              </Form.Item>
              <Form.Item>
                <Button
                  loading={loading}
                  htmlType="submit"
                  type="primary"
                  style={{ fontWeight: "500" }}
                  className={btnStyles.mdBtn}
                  onClick={() => {
                    if (formRef.current) {
                      formRef.current.submit();
                    }
                  }}
                >
                  Save
                </Button>
              </Form.Item>
            </div>
          )
        }
        onCancel={() => setOpen(false)}
      >
        <Form
          layout="vertical"
          onFinish={onFinish}
          form={form}
          ref={formRef}
          className="investigation-modal-form"
          style={{ marginTop: isMobile ? 0 : "20px" }}
        >
          <InvestigationSearch
            searchedText={searchedText}
            setSearchedText={setSearchedText}
            setSelectedId={setSelectedId}
          />
          {(searchedText || selectedId) && (
            <Form.Item
              label="Add Notes"
              name="comments"
              style={{ marginTop: isMobile ? 0 : "20px" }}
            >
              <Input.TextArea
                disabled={["Nurse", "Receptionist"].includes(defaultRole)}
                rows={isMobile ? 3 : 1}
                className={`${inputStyles.mdInput} mdInput investigation-modal-notes`}
              />
            </Form.Item>
          )}
          {editData?.id && (
            <Form.Item label="Status" name="status">
              <Select onChange={(value) => setSelectedStatus(value)}>
                {StatusOptions?.map((item) => (
                  <Option key={item?.value} value={item?.value}>
                    {item?.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}
          {selectedStatus === "Report_Delivered" && (
            <>
              <Form.Item
                label="Delivered Date"
                name="delivery_on"
                rules={[
                  {
                    required: true,
                    message: "Please enter report delivered date",
                  },
                ]}
              >
                <DatePicker
                  className={`${inputStyles.mdDateInput} investigation-date-picker`}
                  disabledDate={disabledDate}
                  allowClear
                  clearIcon={
                    <CloseCircleFilled
                      className="investigation-date-clear"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                    />
                  }
                  style={{ width: "100%" }}
                  {...mobilePickerProps}
                  popupClassName={datePickerPopupClass}
                  placement="bottomLeft"
                  getPopupContainer={() => document.body}
                  onOpenChange={(open) => {
                    if (isCompactPicker) alignInvestigationDatePickerBelow(open);
                  }}
                />
              </Form.Item>

              <Form.Item
                label="Upload Investigations"
                name="upload"
                rules={[
                  {
                    required: !uploadFile || uploadFile.length === 0,
                    message: "Please upload investigations",
                  },
                ]}
              >
                <Dragger
                  className="investigation-modal-upload"
                  accept=".png,.jpg,.jpeg,.svg,application/pdf"
                  {...uploadProps}
                  multiple
                  showUploadList={{
                    showRemoveIcon: false,
                  }}
                  customRequest={dummyRequest}
                >
                  <p className="ant-upload-drag-icon">
                    <CameraOutlined />
                  </p>

                  <p className="ant-upload-text">
                    <span style={{ color: "#406AFF" }}>Click to Capture </span>{" "}
                    or upload
                  </p>
                  <p className="ant-upload-hint">
                    PDF, SVG, JPEG, JPG, PNG ({MAX_FILE_SIZE_MB}MB each)
                  </p>
                </Dragger>
                {uploadFile.map((item, index) => (
                  <div key={index} className="investigation-modal-file-item">
                    <span className="investigation-modal-file-name">
                      {item.path.replace("curengo/", "")}
                    </span>
                    <Button
                      type="link"
                      icon={<DeleteOutlined />}
                      onClick={() => handleRemoveFile(item)}
                    />
                  </div>
                ))}
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>
    </>
  );
};

export default InvestigationModal;
